import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { getProducts, getCategories, createCategory, updateCategory, deleteCategory, createProduct, updateProduct, deleteProduct } from '@/services/firestore'
import { friendlyError } from '@/utils/friendlyError'
import { uploadProductImage } from '@/services/cloudinary'
import {
  Search, Plus, Package, Edit3, Trash2, X, Save, ImagePlus,
} from 'lucide-react'

export const Route = createFileRoute('/admin/menu')({
  component: ProductsPage,
})

interface Product { id: string; name: string; code: string; hsCode?: string; taxRate?: number; price: number; costPrice?: number; stock: number; minStock?: number; categoryId: string; unit: string; imageUrl?: string;[k: string]: any }
interface Category { id: string; name: string; sortOrder?: number;[k: string]: any }

function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterCat, setFilterCat] = useState('all')
  const [activeTab, setActiveTab] = useState<'products' | 'categories'>('products')

  // Product Form State
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Product | null>(null)
  const [form, setForm] = useState({ name: '', code: '', hsCode: '', taxRate: '18', price: '', costPrice: '', stock: '', minStock: '5', categoryId: '', unit: 'PCS' })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Category Form State
  const [showCatForm, setShowCatForm] = useState(false)
  const [editingCat, setEditingCat] = useState<Category | null>(null)
  const [catForm, setCatForm] = useState({ name: '', sortOrder: '0' })
  const [savingCat, setSavingCat] = useState(false)

  useEffect(() => { load() }, [])
  const load = async () => {
    try {
      const [p, c] = await Promise.all([getProducts(), getCategories()])
      setProducts(p as Product[]); setCategories(c as Category[])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const filtered = products.filter(p => {
    const s = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase())
    const c = filterCat === 'all' || p.categoryId === filterCat
    return s && c
  })

  const openNew = () => {
    setEditing(null)
    setForm({ name: '', code: '', hsCode: '', taxRate: '18', price: '', costPrice: '', stock: '', minStock: '5', categoryId: categories[0]?.id || '', unit: 'PCS' })
    setImageFile(null); setImagePreview('')
    setShowForm(true)
  }
  const openEdit = (p: Product) => {
    setEditing(p)
    setForm({ name: p.name, code: p.code, hsCode: p.hsCode || '', taxRate: String(p.taxRate ?? 18), price: String(p.price), costPrice: String(p.costPrice || ''), stock: String(p.stock), minStock: String(p.minStock || 5), categoryId: p.categoryId, unit: p.unit || 'PCS' })
    setImageFile(null); setImagePreview(p.imageUrl || '')
    setShowForm(true)
  }
  const handleSave = async () => {
    const data: any = { name: form.name, code: form.code, hsCode: form.hsCode, taxRate: Number(form.taxRate), price: Number(form.price), costPrice: Number(form.costPrice) || 0, stock: Number(form.stock), minStock: Number(form.minStock), categoryId: form.categoryId, unit: form.unit }
    try {
      setUploading(true)
      // Upload image to Cloudinary if a new file was selected
      if (imageFile) {
        const imgUrl = await uploadProductImage(imageFile, form.code || form.name.replace(/\s+/g, '_'))
        data.imageUrl = imgUrl
      }
      if (editing) { await updateProduct(editing.id, data) } else { await createProduct(data) }
      setShowForm(false); setImageFile(null); setImagePreview(''); load()
    } catch (err: any) { alert(friendlyError(err, 'Could not save product')) }
    finally { setUploading(false) }
  }
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this product?')) return
    try { await deleteProduct(id); load() } catch (err: any) { alert(friendlyError(err, 'Could not delete product')) }
  }

  // Category Handlers
  const openNewCat = () => { setEditingCat(null); setCatForm({ name: '', sortOrder: String(categories.length) }); setShowCatForm(true) }
  const openEditCat = (c: Category) => { setEditingCat(c); setCatForm({ name: c.name, sortOrder: String(c.sortOrder || 0) }); setShowCatForm(true) }
  const handleSaveCat = async () => {
    if (!catForm.name) return alert('Category name is required')
    try {
      setSavingCat(true)
      const data = { name: catForm.name, sortOrder: Number(catForm.sortOrder) || 0, isActive: true }
      if (editingCat) { await updateCategory(editingCat.id, data) } else { await createCategory(data) }
      setShowCatForm(false); load()
    } catch (err: any) { alert(friendlyError(err, 'Could not save category')) }
    finally { setSavingCat(false) }
  }
  const handleDeleteCat = async (id: string) => {
    if (products.some(p => p.categoryId === id)) return alert('Cannot delete category because it has products linked to it.')
    if (!confirm('Delete this category?')) return
    try { await deleteCategory(id); load() } catch (err: any) { alert(friendlyError(err, 'Could not delete category')) }
  }

  const getCatName = (id: string) => categories.find(c => c.id === id)?.name || '-'
  const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  if (loading) return (
    <div className="p-4 lg:p-5 bg-[#f0f5ff] min-h-screen animate-pulse">
      <div className="flex items-center justify-between mb-4"><div className="h-5 w-32 bg-slate-200 rounded-lg" /><div className="h-8 w-28 bg-slate-200 rounded-lg" /></div>
      <div className="bg-white rounded-xl h-12 border border-slate-200/60 mb-3" />
      <div className="bg-white rounded-xl h-96 border border-slate-200/60" />
    </div>
  )

  return (
    <div className="p-4 pt-14 lg:pt-5 lg:p-5 bg-[#f0f5ff] min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-600" />
            Inventory & Menu
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Manage products and categories.</p>
        </div>

        <div className="flex bg-white rounded-lg border p-1 shadow-sm">
          <button onClick={() => setActiveTab('products')} className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${activeTab === 'products' ? 'bg-blue-50 text-blue-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}>Products ({products.length})</button>
          <button onClick={() => setActiveTab('categories')} className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${activeTab === 'categories' ? 'bg-blue-50 text-blue-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}>Categories ({categories.length})</button>
        </div>

        {activeTab === 'products' ? (
          <button onClick={openNew} className="flex items-center justify-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-xl text-[11px] font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-200">
            <Plus className="w-3.5 h-3.5" />Add Product
          </button>
        ) : (
          <button onClick={openNewCat} className="flex items-center justify-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-xl text-[11px] font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-200">
            <Plus className="w-3.5 h-3.5" />Add Category
          </button>
        )}
      </div>

      {
        activeTab === 'products' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl border border-slate-200/60 p-3 mb-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-slate-50/50 transition-all" />
              </div>
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                className="px-2.5 py-2 sm:py-1.5 rounded border border-slate-200 text-[11px] bg-slate-50 focus:outline-none">
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] min-w-[700px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
                      <th className="text-left px-3 py-2 font-semibold">Product</th>
                      <th className="text-left px-3 py-2 font-semibold">Code</th>
                      <th className="text-left px-3 py-2 font-semibold">HS Code</th>
                      <th className="text-left px-3 py-2 font-semibold">Category</th>
                      <th className="text-center px-3 py-2 font-semibold">Tax %</th>
                      <th className="text-right px-3 py-2 font-semibold">Cost</th>
                      <th className="text-right px-3 py-2 font-semibold">Price</th>
                      <th className="text-right px-3 py-2 font-semibold">Stock</th>
                      <th className="text-center px-3 py-2 font-semibold">Status</th>
                      <th className="text-center px-3 py-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, idx) => (
                      <tr key={p.id} className={`border-b border-slate-100/80 hover:bg-blue-50/30 transition-colors duration-150 ${idx % 2 === 1 ? 'bg-slate-50/30' : ''}`}>
                        <td className="px-3 py-2 font-semibold text-slate-800">
                          <div className="flex items-center gap-2">
                            {p.imageUrl ? (
                              <img src={p.imageUrl} alt={p.name} className="w-8 h-8 rounded-lg object-cover border border-slate-200" />
                            ) : (
                              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                <Package className="w-4 h-4 text-slate-300" />
                              </div>
                            )}
                            {p.name}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-500 font-mono">{p.code || '-'}</td>
                        <td className="px-3 py-2 text-slate-500 font-mono">{p.hsCode || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{getCatName(p.categoryId)}</td>
                        <td className="px-3 py-2 text-center text-slate-500 font-semibold">{p.taxRate ?? 18}%</td>
                        <td className="px-3 py-2 text-right text-slate-500">{p.costPrice ? f(p.costPrice) : '-'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-800">{f(p.price)}</td>
                        <td className="px-3 py-2 text-right font-bold" style={{ color: p.stock <= (p.minStock || 5) ? '#ef4444' : p.stock <= 10 ? '#f59e0b' : '#22c55e' }}>
                          {p.stock}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {p.stock === 0 ? (
                            <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">OUT</span>
                          ) : p.stock <= (p.minStock || 5) ? (
                            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">LOW</span>
                          ) : (
                            <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">OK</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-center gap-1.5">
                            <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 p-1.5 rounded-lg" title="Edit">
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 p-1.5 rounded-lg" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-12">
                        <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-slate-400 text-xs">No products found</p>
                        <p className="text-slate-300 text-[10px] mt-0.5">Try adjusting your search or filters</p>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div> {/* end overflow-x-auto */}
            </div>

            {/* Stats Footer */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <div className="bg-white rounded-xl border border-slate-200/60 p-3 text-center hover:shadow-md transition-all duration-200">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Products</div>
                <div className="text-lg font-bold text-slate-800">{products.length}</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200/60 p-3 text-center hover:shadow-md transition-all duration-200">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Low Stock</div>
                <div className="text-lg font-bold text-amber-500">{products.filter(p => p.stock > 0 && p.stock <= (p.minStock || 5)).length}</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200/60 p-3 text-center hover:shadow-md transition-all duration-200">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Out of Stock</div>
                <div className="text-lg font-bold text-red-500">{products.filter(p => p.stock === 0).length}</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200/60 p-3 text-center hover:shadow-md transition-all duration-200">
                <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Value</div>
                <div className="text-lg font-bold text-slate-800">Rs {(products.reduce((s, p) => s + p.price * p.stock, 0)).toLocaleString()}</div>
              </div>
            </div>
          </>
        )
      }

      {/* Categories View */}
      {
        activeTab === 'categories' && (
          <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden shadow-sm">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-semibold w-16">Rank</th>
                  <th className="text-left px-4 py-3 font-semibold">Category Name</th>
                  <th className="text-center px-4 py-3 font-semibold w-24">Products</th>
                  <th className="text-center px-4 py-3 font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-mono text-slate-500">{c.sortOrder || 0}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{c.name}</td>
                    <td className="px-4 py-3 text-center text-slate-500">{products.filter(p => p.categoryId === c.id).length}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEditCat(c)} className="text-slate-400 hover:text-blue-600 p-1 rounded-md transition-colors"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteCat(c.id)} className="text-slate-400 hover:text-red-600 p-1 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-500">No categories found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )
      }

      {/* Add/Edit Product Modal */}
      {
        showForm && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-bold text-slate-800">{editing ? 'Edit Product' : 'New Product'}</h3>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-4 space-y-3">
                <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Product Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Code</label><input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">HS Code (FBR)</label><input value={form.hsCode} onChange={e => setForm({ ...form, hsCode: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Sales Tax %</label><input type="number" value={form.taxRate} onChange={e => setForm({ ...form, taxRate: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] font-bold focus:outline-none focus:border-blue-500" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Selling Price *</label><input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Cost Price</label><input type="number" value={form.costPrice} onChange={e => setForm({ ...form, costPrice: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Stock *</label><input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Min Stock</label><input type="number" value={form.minStock} onChange={e => setForm({ ...form, minStock: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500" /></div>
                  <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Unit</label>
                    <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none bg-white">
                      <option>PCS</option><option>KG</option><option>LTR</option><option>MTR</option><option>PACK</option><option>BOX</option>
                    </select>
                  </div>
                </div>
                <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Category</label>
                  <select value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value })} className="w-full mt-1 px-3 py-1.5 rounded border border-slate-200 text-[12px] focus:outline-none bg-white">
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {/* Product Image Upload */}
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Product Image</label>
                  <div className="mt-1 flex items-center gap-3">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="w-16 h-16 rounded-xl object-cover border-2 border-blue-200 shadow-sm" />
                        <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); if (fileInputRef.current) fileInputRef.current.value = '' }}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] font-bold hover:bg-red-600">✕</button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                        <ImagePlus className="w-5 h-5 text-slate-300" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={e => {
                        const file = e.target.files?.[0]
                        if (file) {
                          setImageFile(file)
                          setImagePreview(URL.createObjectURL(file))
                        }
                      }} className="hidden" id="product-image-input" />
                      <label htmlFor="product-image-input"
                        className="cursor-pointer inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors">
                        <ImagePlus className="w-3.5 h-3.5" />{imagePreview ? 'Change Image' : 'Upload Image'}
                      </label>
                      <p className="text-[9px] text-slate-400 mt-1">JPG, PNG, WebP (max 5MB)</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
                <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded text-[11px] font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
                <button onClick={handleSave} disabled={uploading} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-1.5 rounded text-[11px] font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {uploading ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</> : <><Save className="w-3 h-3" />Save</>}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Add/Edit Category Modal */}
      {
        showCatForm && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl animate-in fade-in zoom-in-95">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-bold text-slate-800">{editingCat ? 'Edit Category' : 'New Category'}</h3>
                <button onClick={() => setShowCatForm(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Category Name *</label>
                  <input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} autoFocus
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Sort Order (Rank)</label>
                  <input type="number" value={catForm.sortOrder} onChange={e => setCatForm({ ...catForm, sortOrder: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-500" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
                <button onClick={() => setShowCatForm(false)} className="px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200 rounded">Cancel</button>
                <button onClick={handleSaveCat} disabled={savingCat} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-1.5 rounded text-[11px] font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {savingCat ? 'Saving...' : <><Save className="w-3 h-3" />Save</>}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  )
}